/** At least one of `name` / `value` must be present (enforced by schema + `isLabelFilter`). */
export type LabelFilter = {
  name?: string;
  value?: string;
};

/** Label filter parsed from a display token, which always carries a name. */
export type NamedLabelFilter = LabelFilter & { name: string };

export const isLabelFilter = (value: unknown): value is LabelFilter => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as { name?: unknown; value?: unknown };
  const hasName = typeof candidate.name === 'string';
  const hasValue = typeof candidate.value === 'string';
  const nameAbsent = candidate.name === undefined;
  const valueAbsent = candidate.value === undefined;

  if (hasName && (hasValue || valueAbsent)) {
    return true;
  }

  return nameAbsent && hasValue;
};

export const isLabelFilterArray = (value: unknown): value is LabelFilter[] =>
  Array.isArray(value) && value.every(isLabelFilter);

export const isNameOnlyLabelFilter = (
  filter: LabelFilter,
): filter is { name: string; value?: undefined } =>
  typeof filter.name === 'string' && filter.value === undefined;

export const displayTokenToLabelFilter = (token: string): NamedLabelFilter => {
  // Limit 2 keeps extra colons in the value (e.g. "url: https://x").
  const [name = '', value] = token.split(':', 2).map((part) => part.trim());

  if (value === undefined) {
    return { name };
  }

  return { name, value };
};

export const parseDisplayTokensToLabelFilters = (
  raw: string,
): NamedLabelFilter[] =>
  raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token !== '')
    .map(displayTokenToLabelFilter)
    .filter((filter) => filter.name !== '');

export const labelFilterToDisplayToken = (filter: LabelFilter): string => {
  if (filter.name === undefined) {
    return filter.value ?? '';
  }

  if (filter.value === undefined) {
    return filter.name;
  }

  if (filter.value === '') {
    return `${filter.name}:`;
  }

  return `${filter.name}: ${filter.value}`;
};

export const labelFiltersToDisplayTokens = (filters: LabelFilter[]): string =>
  filters.map(labelFilterToDisplayToken).join(', ');

export const labelFilterToJsonbObject = (
  filter: LabelFilter,
): Record<string, string> => {
  const result: Record<string, string> = {};

  if (filter.name !== undefined) {
    result.name = filter.name;
  }

  if (filter.value !== undefined) {
    result.value = filter.value;
  }

  return result;
};

export type ScalarFilterTupleValue =
  | boolean
  | string
  | number
  | null
  | (boolean | string | number | null)[];

export const asScalarFilterTupleValue = (
  value: ScalarFilterTupleValue | LabelFilter[] | undefined,
): ScalarFilterTupleValue | undefined => {
  if (value === undefined || isLabelFilterArray(value)) {
    return undefined;
  }

  return value;
};
